// hooks/useCarWashData.ts
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CarDetail, CarDetailState } from "../../home/components/types";
import api from "../../../../../features/auth/axiosConfig";
import { Category, Product } from "src/shared/services/interfacev2";

export const useCarWashData = () => {
  const [orderlist, setOrderList] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [customerData, setCustomerData] = useState<CarDetail | null>(null); // Changed to nullable
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true); // Added loading state

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const customerId = searchParams.get("id");

  useEffect(() => {
    fetchData();
  }, []);

  // Update filtered products when orderlist or activeCategoryId changes
  useEffect(() => {
    if (activeCategoryId) {
      filterProductsByCategory(activeCategoryId);
    } else if (orderlist.length > 0) {
      setFilteredProducts(orderlist);
    }
  }, [activeCategoryId, orderlist]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      await Promise.all([fetchProducts(), fetchCategories(), fetchCustomerData()]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await api.get(`/app/pos/v2/product`);
      const userProducts: Product[] = response.data.results || [];
      setOrderList(userProducts);

      if (userProducts.length > 0) {
        setFilteredProducts(userProducts);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setOrderList([]);
      setFilteredProducts([]);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get(`/app/pos/v2/category?include_deleted=False&pageNumber=&pageSize=`);
      const categoryDetails: Category[] = response.data.categoryDetails || [];
      setCategories(categoryDetails);

      if (categoryDetails.length > 0) {
        const firstCategoryId = categoryDetails[0]._id.$oid;
        setActiveCategoryId(firstCategoryId);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      setCategories([]);
    }
  };

  const fetchCustomerData = async () => {
    if (!customerId) {
      setCustomerData(null);
      return;
    }

    try {
      const response = await api.get(`/app/pos/v2/shopping_cart/${customerId}`);

      // Check if we have data in the expected format
      if (response.data && response.data.result && response.data.result.length > 0) {
        const carDetail = response.data.result[0];
        setCustomerData(carDetail);
      } else {
        setCustomerData(null);
      }
    } catch (error) {
      console.error("Error fetching customer data:", error);
      setCustomerData(null);
    }
  };

  const filterProductsByCategory = (categoryId: string) => {
    const filtered = orderlist.filter((item) => item.categoryId === categoryId);
    setFilteredProducts(filtered);
  };

  const handleCategoryChange = (categoryId: string) => {
    setActiveCategoryId(categoryId);
  };

  const showAllProducts = () => {
    setActiveCategoryId(null);
    setFilteredProducts(orderlist);
  };

  return {
    orderlist,
    categories,
    customerData, // This can be null
    activeCategoryId,
    filteredProducts,
    customerId,
    isLoading, // Return loading state
    handleCategoryChange,
    showAllProducts,
    fetchCustomerData, // Expose for manual refresh if needed
  };
};
